/**
 * Outlook 郵件適配器
 * 實作 Outlook 郵件的收件人新增功能
 */

import { BaseAdapter } from './baseAdapter';

export class OutlookAdapter extends BaseAdapter {
  /**
   * 檢查當前環境是否支援 Outlook 適配器
   * @returns {boolean}
   */
  isSupported() {
    const item = Office?.context?.mailbox?.item;
    if (!item) return false;

    // 如果是 Appointment（活動/會議），交給 CalendarAdapter
    const itemType = item.itemType;
    const appointmentType = Office?.MailboxEnums?.ItemType?.Appointment;
    if (itemType && appointmentType && itemType === appointmentType) {
      return false;
    }

    // 郵件撰寫通常會有 to/cc/bcc
    return !!(item.to && item.cc && item.bcc);
  }

  /**
   * 獲取適配器名稱
   * @returns {string}
   */
  getName() {
    return 'Outlook';
  }

  /**
   * 新增收件人到 Outlook 郵件
   * @param {Array} recipients - 收件人列表，格式：[{displayName: string, emailAddress: string}]
   * @param {string} type - 類型："to" | "cc" | "bcc"
   * @returns {Promise<void>}
   */
  async addRecipients(recipients, type) {
    if (!this.isSupported()) {
      throw new Error('Outlook context is not available');
    }

    // 過濾有效的收件人
    const validRecipients = recipients
      .map((item) => ({
        displayName: item.displayName,
        emailAddress: item.mail || item.emailAddress || item.userPrincipalName,
      }))
      .filter((r) => r.emailAddress);

    // 去重
    const uniqueRecipientMap = new Map();
    validRecipients.forEach((r) => {
      const email = r.emailAddress;
      if (!uniqueRecipientMap.has(email)) {
        uniqueRecipientMap.set(email, r);
      }
    });

    const uniqueRecipients = Array.from(uniqueRecipientMap.values());

    if (uniqueRecipients.length === 0) {
      console.warn("No valid recipients found.");
      return;
    }

    return new Promise((resolve, reject) => {
      const item = Office.context.mailbox.item;
      let addMethod;

      switch (type) {
        case 'to':
          addMethod = item.to.addAsync.bind(item.to);
          break;
        case 'cc':
          addMethod = item.cc.addAsync.bind(item.cc);
          break;
        case 'bcc':
          addMethod = item.bcc.addAsync.bind(item.bcc);
          break;
        default:
          reject(new Error(`Invalid message type: ${type}`));
          return;
      }

      addMethod(uniqueRecipients, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to add recipients:", asyncResult.error.message);
          reject(new Error(asyncResult.error.message));
        } else {
          console.log("Recipients added successfully.");
          resolve();
        }
      });
    });
  }
}

